import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Alert, App, Button, Card, Col, Form, Input, Modal, Popconfirm, Progress, Row, Select, Skeleton, Space, Switch, Table, Tag, Tooltip, Typography } from 'antd';
import { CheckCircleOutlined, ClockCircleOutlined, CopyOutlined, DeleteOutlined, PauseCircleOutlined, PlayCircleOutlined, PlusOutlined, SendOutlined, SettingOutlined, UserOutlined } from '@ant-design/icons';
import { fmtRate, type DomainWarmupItem, type WarmupMessageItem, type WarmupPartnerItem, type WarmupSettings } from '@crm/shared';
import { PageHeader } from '../../components/PageHeader';
import { CardTitle } from '../../components/StatCard';
import { NameCell } from '../../components/NameCell';
import { EmptyState, LoadError, tableLocale, tableLoading } from '../../components/states';
import { errorMessage } from '../../api/client';
import { useAgents } from '../../features/email/api';
import {
  useAddWarmupPartners,
  useDeleteWarmupPartner,
  usePauseDomain,
  useSendWarmupNow,
  useUpdateWarmupPartner,
  useWarmupDomains,
  useWarmupMessages,
  useWarmupPartners,
  useWarmupSettings,
} from '../../features/warmup/api';
import { fmtDateTime, plural } from '../../utils/format';
import { palette } from '../../app/theme';
import { PHASE_TAG } from '../admin/WarmupSettingsPage';

const PARTNER_NOTE = `Hi! We're warming up our new email addresses so customers' inboxes trust them. Over the next few weeks you'll get a few short emails a day from our agents. Please:
1. Reply to each one with a sentence or two (anything natural is fine).
2. If one lands in Spam or Promotions, move it to your Inbox and mark it "Not spam".
3. Star or mark a few as important now and then.
Thanks for the help!`;

/** Tools → Email warm-up: each domain's progress today, the warm-up partners and the emails they were sent. */
export function WarmupPage() {
  const domains = useWarmupDomains();
  const settings = useWarmupSettings();
  const [sendOpen, setSendOpen] = useState(false);
  return (
    <div className="page page-wide">
      <PageHeader
        title="Email warm-up"
        subtitle="How far each sending domain is, what it sent today, its bounce and spam rates, and the warm-up emails our own people reply to."
        extra={
          <Space wrap>
            <Link to="/settings/warmup">
              <Button icon={<SettingOutlined />}>Limits and schedule</Button>
            </Link>
            <Button type="primary" icon={<SendOutlined />} onClick={() => setSendOpen(true)}>Send a warm-up email</Button>
          </Space>
        }
      />
      {domains.isError ? (
        <Card style={{ marginBottom: 24 }}>
          <LoadError error={domains.error} onRetry={domains.refetch} title="Could not load domains" />
        </Card>
      ) : !domains.data ? (
        <Card style={{ marginBottom: 24 }}>
          <Skeleton active />
        </Card>
      ) : !domains.data.length ? (
        <Card style={{ marginBottom: 24 }}>
          <EmptyState description="No sending domains yet." action={<Link to="/settings/domains"><Button type="primary">Add a domain</Button></Link>} />
        </Card>
      ) : (
        <Row gutter={[24, 24]} style={{ marginBottom: 24 }}>
          {domains.data.map((d) => (
            <Col key={d.id} xs={24} xl={domains.data.length > 1 ? 12 : 24}>
              <DomainCard domain={d} thresholds={settings.data} />
            </Col>
          ))}
        </Row>
      )}
      <PartnersCard />
      <MessagesCard />
      <SendNowModal open={sendOpen} onClose={() => setSendOpen(false)} />
    </div>
  );
}

function rateColor(rate: number, limit: number | undefined) {
  if (!limit) return undefined;
  return rate >= limit ? palette.error : rate >= limit / 2 ? palette.warning : palette.success;
}

function DomainCard({ domain: d, thresholds: t }: { domain: DomainWarmupItem; thresholds?: WarmupSettings }) {
  const { message } = App.useApp();
  const pause = usePauseDomain();
  const s = d.signals;
  const enough = !t || s.sent >= t.minSent;
  const pct = d.todayLimit ? Math.min(100, Math.round((d.sentToday / d.todayLimit) * 100)) : 0;
  const replyRate = d.partnerSent7d ? Math.round((d.partnerReplied7d / d.partnerSent7d) * 100) : null;
  const toggle = (on: boolean) =>
    pause
      .mutateAsync({ id: d.id, pause: on })
      .then(() => message.success(on ? `Sending paused for ${d.domain}` : `Sending resumed for ${d.domain}`))
      .catch((e) => message.error(errorMessage(e)));

  const phase =
    d.phase === 'warming' ? `${PHASE_TAG.warming.label} · day ${(d.day ?? 0) + 1} of ~${d.daysToTarget + 1}` : PHASE_TAG[d.phase].label;

  return (
    <Card
      style={{ height: '100%' }}
      title={<CardTitle title={d.domain} subtitle={`${plural(d.agentCount, 'agent')}${d.healthSummary ? ` · last health check: ${d.healthSummary.pass} passed${d.healthSummary.warn ? `, ${d.healthSummary.warn} to look at` : ''}${d.healthSummary.fail ? `, ${d.healthSummary.fail} failing` : ''}` : ' · not health-checked yet'}`} />}
      extra={
        <Space size={4} wrap style={{ justifyContent: 'flex-end' }}>
          {d.sendingPausedAt ? <Tag color="error">Paused</Tag> : <Tag color={PHASE_TAG[d.phase].color}>{phase}</Tag>}
        </Space>
      }
    >
      {d.sendingPausedAt && (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          title={`Sending paused ${fmtDateTime(d.sendingPausedAt)}`}
          description={
            <span>
              {d.sendingPausedReason}. {d.heldCampaigns ? `${plural(d.heldCampaigns, 'campaign')} waiting. ` : ''}Fix the cause first (an old or bought list, a broken signup form), then resume. Rates start counting again from then.
            </span>
          }
          action={
            <Popconfirm title={`Resume sending from ${d.domain}?`} description="Waiting campaigns carry on right away, within today's limit." okText="Resume" onConfirm={() => toggle(false)}>
              <Button size="small" type="primary" icon={<PlayCircleOutlined />} loading={pause.isPending}>Resume</Button>
            </Popconfirm>
          }
        />
      )}
      {!d.sendingPausedAt && d.signalProblem && !t?.autoPause && (
        <Alert type="warning" showIcon style={{ marginBottom: 16 }} title={d.signalProblem} description="Automatic pausing is off, so sending carries on. Consider pausing until you find the cause." />
      )}
      {!d.inboundEnabled && d.warmupEnabled && (
        <Alert type="warning" showIcon style={{ marginBottom: 16 }} title="Receiving is off" description={<span>Partner replies can't reach the CRM, so they aren't counted. Turn receiving on in <Link to="/settings/domains">Settings → Email domains</Link>.</span>} />
      )}
      {!d.verified && <Alert type="warning" showIcon style={{ marginBottom: 16 }} title="Not verified in Resend" description={<span>Nothing sends until it is. See <Link to={`/tools/domain-health?domain=${d.id}`}>Domain health</Link> for the missing records.</span>} />}

      <Typography.Text type="secondary" style={{ fontSize: 13 }}>Sent today</Typography.Text>
      {d.todayLimit === null ? (
        <div style={{ fontSize: 22, fontWeight: 600 }}>
          {d.sentToday.toLocaleString()} <Typography.Text type="secondary" style={{ fontSize: 13, fontWeight: 400 }}>no daily limit</Typography.Text>
        </div>
      ) : (
        <>
          <div style={{ fontSize: 22, fontWeight: 600 }}>
            {d.sentToday.toLocaleString()} <Typography.Text type="secondary" style={{ fontSize: 13, fontWeight: 400 }}>of {d.todayLimit.toLocaleString()} today · count starts over {fmtDateTime(d.resetsAt)}</Typography.Text>
          </div>
          <Progress percent={pct} showInfo={false} status={pct >= 100 ? 'exception' : 'active'} strokeColor={pct >= 100 ? palette.warning : undefined} />
        </>
      )}
      {d.heldCampaigns > 0 && !d.sendingPausedAt && (
        <Typography.Text type="secondary" style={{ display: 'block', fontSize: 13 }}>
          <ClockCircleOutlined /> {plural(d.heldCampaigns, 'campaign')} waiting for tomorrow's limit
        </Typography.Text>
      )}

      <Row gutter={16} style={{ marginTop: 16 }}>
        <Col span={8}>
          <Metric label="Bounce rate" value={enough ? fmtRate(s.bounceRate) : '—'} color={enough ? rateColor(s.bounceRate, t?.maxBounceRate) : undefined} note={t ? `pauses at ${fmtRate(t.maxBounceRate)}` : undefined} />
        </Col>
        <Col span={8}>
          <Metric label="Spam complaints" value={enough ? fmtRate(s.complaintRate) : '—'} color={enough ? rateColor(s.complaintRate, t?.maxComplaintRate) : undefined} note={t ? `pauses at ${fmtRate(t.maxComplaintRate)}` : undefined} />
        </Col>
        <Col span={8}>
          <Metric label="Partner replies" value={replyRate === null ? '—' : `${replyRate}%`} color={replyRate === null ? undefined : replyRate >= 50 ? palette.success : palette.warning} note={`${d.partnerReplied7d} of ${d.partnerSent7d}, 7 days`} />
        </Col>
      </Row>
      <Typography.Text type="secondary" style={{ display: 'block', fontSize: 12, marginTop: 8 }}>
        {enough
          ? `${s.sent.toLocaleString()} emails in the last ${s.windowDays} days${s.from && new Date(s.from).getTime() > Date.now() - s.windowDays * 86_400_000 + 60_000 ? ` (since resumed ${fmtDateTime(s.from)})` : ''}.`
          : `${s.sent.toLocaleString()} of ${t?.minSent ?? 0} emails needed before rates count.`}{' '}
        {d.warmupEnabled && `${d.partnerSentToday} warm-up ${d.partnerSentToday === 1 ? 'email' : 'emails'} today (${d.warmupPartnerDaily} per agent each weekday).`}
      </Typography.Text>

      <Space wrap style={{ marginTop: 16 }}>
        <Link to={`/tools/domain-health?domain=${d.id}`}>
          <Button size="small" icon={<CheckCircleOutlined />}>Domain health</Button>
        </Link>
        {!d.sendingPausedAt && (
          <Popconfirm title={`Pause sending from ${d.domain}?`} description="Campaigns wait and warm-up emails stop until you resume. One-to-one emails still go out." okText="Pause" okButtonProps={{ danger: true }} onConfirm={() => toggle(true)}>
            <Button size="small" danger icon={<PauseCircleOutlined />} loading={pause.isPending}>Pause sending</Button>
          </Popconfirm>
        )}
      </Space>
    </Card>
  );
}

function Metric({ label, value, color, note }: { label: string; value: string; color?: string; note?: string }) {
  return (
    <div>
      <Typography.Text type="secondary" style={{ fontSize: 13 }}>{label}</Typography.Text>
      <div style={{ fontSize: 20, fontWeight: 600, color: color ?? palette.text }}>{value}</div>
      {note && <Typography.Text type="secondary" style={{ fontSize: 12 }}>{note}</Typography.Text>}
    </div>
  );
}

function PartnersCard() {
  const { message } = App.useApp();
  const partners = useWarmupPartners();
  const update = useUpdateWarmupPartner();
  const remove = useDeleteWarmupPartner();
  const add = useAddWarmupPartners();
  const [open, setOpen] = useState(false);
  const [form] = Form.useForm<{ text: string }>();

  const submit = async ({ text }: { text: string }) => {
    try {
      const r = await add.mutateAsync(text);
      const parts = [`${plural(r.added, 'partner')} added`, r.existing && `${r.existing} already on the list`, r.agents && `${plural(r.agents, 'agent address', 'agent addresses')} skipped`].filter(Boolean);
      message.success(parts.join(', '));
      if (r.invalid.length) message.warning(`Not email addresses: ${r.invalid.slice(0, 5).join(', ')}`);
      setOpen(false);
    } catch (e) {
      message.error(errorMessage(e));
    }
  };

  return (
    <Card
      style={{ marginBottom: 24 }}
      title={<CardTitle title="Warm-up partners" subtitle="Our own people. Each warming agent emails them a few times every weekday, and they reply by hand. Real replies and “Not spam” clicks are what build a new domain's reputation." />}
      extra={<Button icon={<PlusOutlined />} onClick={() => { form.resetFields(); setOpen(true); }}>Add partners</Button>}
    >
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        title="What to ask your partners"
        description={
          <div>
            <Typography.Paragraph style={{ whiteSpace: 'pre-line', marginBottom: 8 }}>{PARTNER_NOTE}</Typography.Paragraph>
            <Typography.Text copyable={{ text: PARTNER_NOTE, icon: [<CopyOutlined key="c" />, <CheckCircleOutlined key="d" />], tooltips: ['Copy the message', 'Copied'] }}>Copy this message to send them</Typography.Text>
            <Typography.Paragraph type="secondary" style={{ fontSize: 13, margin: '8px 0 0' }}>
              Best are addresses at Gmail, Outlook/Hotmail and Yahoo, plus your company mailboxes: those are the inboxes your customers use. 5–20 partners is plenty.
            </Typography.Paragraph>
          </div>
        }
      />
      <Table<WarmupPartnerItem>
        rowKey="id"
        size="middle"
        loading={tableLoading(partners)}
        locale={tableLocale(partners, <EmptyState description="No partners yet. Add a few addresses of people on your team who will reply." action={<Button type="primary" icon={<PlusOutlined />} onClick={() => setOpen(true)}>Add partners</Button>} />)}
        dataSource={partners.data ?? []}
        pagination={false}
        scroll={{ x: 760 }}
        columns={[
          { title: 'Partner', dataIndex: 'email', render: (v: string, p) => <NameCell icon={<UserOutlined />} name={p.name || v} sub={p.name ? v : undefined} /> },
          { title: 'Active', dataIndex: 'active', width: 90, render: (v: boolean, p) => <Switch size="small" checked={v} onChange={(on) => update.mutateAsync({ id: p.id, active: on }).catch((e) => message.error(errorMessage(e)))} /> },
          { title: 'Sent', dataIndex: 'sent', width: 80 },
          { title: 'Replied', dataIndex: 'replied', width: 90 },
          {
            title: 'Reply rate',
            key: 'rate',
            width: 110,
            render: (_, p) => {
              if (!p.sent) return '—';
              const r = Math.round((p.replied / p.sent) * 100);
              return <Typography.Text style={{ color: r >= 50 ? palette.success : palette.warning }}>{r}%</Typography.Text>;
            },
          },
          { title: 'Last reply', dataIndex: 'lastRepliedAt', width: 170, render: (v: string | null, p) => (v ? fmtDateTime(v) : p.sent ? <Typography.Text type="secondary">No replies yet</Typography.Text> : '—') },
          {
            key: 'actions',
            align: 'right',
            width: 60,
            render: (_, p) => (
              <Popconfirm title={`Remove ${p.email}?`} description="Their past warm-up emails stay in the list below." okText="Remove" okButtonProps={{ danger: true }} onConfirm={() => remove.mutateAsync(p.id).catch((e) => message.error(errorMessage(e)))}>
                <Button size="small" type="text" danger icon={<DeleteOutlined />} aria-label="Remove partner" />
              </Popconfirm>
            ),
          },
        ]}
      />
      <Modal title="Add warm-up partners" open={open} onCancel={() => setOpen(false)} onOk={() => form.submit()} okText="Add" confirmLoading={add.isPending} destroyOnHidden>
        <Form form={form} layout="vertical" requiredMark={false} onFinish={submit}>
          <Form.Item name="text" label="Addresses" rules={[{ required: true, message: 'Paste at least one address' }]} extra="One per line, or separated by commas. “Name <email>” uses the name in the greeting.">
            <Input.TextArea autoFocus rows={8} placeholder={'Pat Lee <pat@gmail.com>\njo@outlook.com'} />
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
}

const DELIVERY_TAG: Record<string, { color: string; label: string }> = {
  QUEUED: { color: 'default', label: 'Queued' },
  SENT: { color: 'blue', label: 'Sent' },
  DELIVERED: { color: 'success', label: 'Delivered' },
  OPENED: { color: 'success', label: 'Opened' },
  CLICKED: { color: 'success', label: 'Clicked' },
  BOUNCED: { color: 'error', label: 'Bounced' },
  COMPLAINED: { color: 'error', label: 'Marked spam' },
  FAILED: { color: 'error', label: 'Failed' },
};

function MessagesCard() {
  const [page, setPage] = useState(1);
  const messages = useWarmupMessages({ page, pageSize: 20 });
  return (
    <Card title={<CardTitle title="Warm-up emails" subtitle="Sent on weekdays between 08:00 and 18:00 (workspace time zone), spread over the day. Replies show here and don't go to the inbox." />}>
      <Table<WarmupMessageItem>
        rowKey="id"
        size="middle"
        loading={tableLoading(messages)}
        locale={tableLocale(messages, <EmptyState description="No warm-up emails yet. They start once warm-up is on for a domain and there are active partners." />)}
        dataSource={messages.data?.items ?? []}
        scroll={{ x: 1060 }}
        pagination={{ current: page, pageSize: 20, total: messages.data?.total ?? 0, onChange: setPage, showSizeChanger: false, hideOnSinglePage: true }}
        columns={[
          { title: 'Sent', dataIndex: 'sentAt', width: 150, render: (v: string) => fmtDateTime(v) },
          { title: 'From', key: 'agent', width: 190, render: (_, m) => <NameCell name={m.agent?.name ?? '—'} sub={m.agent?.email ?? m.domain} /> },
          { title: 'To', key: 'to', width: 190, render: (_, m) => <NameCell name={m.partner?.name || m.toAddress} sub={m.partner?.name ? m.toAddress : undefined} /> },
          { title: 'Subject', dataIndex: 'subject', width: 240, ellipsis: true },
          { title: 'Delivery', dataIndex: 'status', width: 110, render: (v: string) => <Tag color={DELIVERY_TAG[v]?.color}>{DELIVERY_TAG[v]?.label ?? v}</Tag> },
          {
            title: 'Reply',
            dataIndex: 'repliedAt',
            width: 180,
            render: (v: string | null, m) =>
              v ? (
                <Tooltip title={m.replySnippet ? `“${m.replySnippet}”` : undefined}>
                  <Tag color="success" icon={<CheckCircleOutlined />}>{fmtDateTime(v)}</Tag>
                </Tooltip>
              ) : (
                <Typography.Text type="secondary">Waiting</Typography.Text>
              ),
          },
        ]}
      />
    </Card>
  );
}

function SendNowModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { message } = App.useApp();
  const agents = useAgents();
  const partners = useWarmupPartners();
  const send = useSendWarmupNow();
  const [form] = Form.useForm<{ agentId: string; partnerId?: string }>();
  return (
    <Modal title="Send a warm-up email now" open={open} onCancel={onClose} onOk={() => form.submit()} okText="Send" confirmLoading={send.isPending} destroyOnHidden>
      <Typography.Paragraph type="secondary">Handy to check that partner replies come back to the CRM. Counts toward the domain's daily limit.</Typography.Paragraph>
      <Form
        form={form}
        layout="vertical"
        requiredMark={false}
        onFinish={(v) =>
          send
            .mutateAsync(v)
            .then((r) => {
              message.success(`Sent “${r.subject}” to ${r.to}`);
              onClose();
            })
            .catch((e) => message.error(errorMessage(e)))
        }
      >
        <Form.Item name="agentId" label="From" rules={[{ required: true, message: 'Choose an agent' }]}>
          <Select placeholder="Agent" options={(agents.data ?? []).filter((a) => a.isActive).map((a) => ({ value: a.id, label: `${a.name} <${a.email}>` }))} showSearch optionFilterProp="label" />
        </Form.Item>
        <Form.Item name="partnerId" label="To" extra="Empty picks the partner this agent emailed least recently.">
          <Select allowClear placeholder="Next partner in turn" options={(partners.data ?? []).filter((p) => p.active).map((p) => ({ value: p.id, label: p.name ? `${p.name} <${p.email}>` : p.email }))} showSearch optionFilterProp="label" />
        </Form.Item>
      </Form>
    </Modal>
  );
}
